// PaymentSheet.jsx
// On-site (embedded) Stripe checkout — NO redirect, no Stripe-hosted page.
// Renders Stripe's <PaymentElement> inside our OWN modal, styled to the app.
// The card data goes straight to Stripe (never our server); to the user it's
// 100% our site.
//
// Flow:
//   1. Fetch the publishable key from GET /api/stripe/status.
//   2. Fetch a clientSecret from POST /api/stripe/subscribe-intent (creates an
//      incomplete subscription server-side).
//   3. Mount <Elements options={{ clientSecret }}> + <PaymentElement>, confirm
//      with redirect:'if_required' so we stay on-page. Success/error inline.

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getApiBase, getAriaUserIdForApi } from './apiBase';

// Load Stripe.js once per publishable key.
let _stripePromise = null;
let _stripePk = '';
function stripePromiseFor(pk) {
  if (!pk) return null;
  if (_stripePromise && _stripePk === pk) return _stripePromise;
  _stripePk = pk;
  _stripePromise = loadStripe(pk);
  return _stripePromise;
}

// Match the dark + yellow HowTo brand so the embedded form looks native.
const APPEARANCE = {
  theme: 'night',
  variables: {
    colorPrimary: '#fcd34d',
    colorBackground: '#16161b',
    colorText: '#ffffff',
    colorTextSecondary: '#9ca3af',
    colorDanger: '#ff6b6b',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    borderRadius: '12px',
    spacingUnit: '4px',
  },
};

// Card ⇄ Bank slider — same sliding-thumb feel as the day/night theme toggle.
// The thumb glides behind the active side; flipping it re-mints the payment
// intent server-side so the form below shows ONLY that method (no inner tabs).
function MethodSwitch({ method, onChange, disabled }) {
  return (
    <div className={`paysheet__method ${disabled ? 'paysheet__method--busy' : ''}`} role="tablist" aria-label="Payment method">
      <span className={`paysheet__method-thumb ${method === 'bank' ? 'paysheet__method-thumb--bank' : ''}`} aria-hidden />
      <button
        type="button" role="tab" aria-selected={method === 'card'}
        className={`paysheet__method-opt ${method === 'card' ? 'paysheet__method-opt--on' : ''}`}
        onClick={() => !disabled && onChange('card')}
      >
        💳 Card
      </button>
      <button
        type="button" role="tab" aria-selected={method === 'bank'}
        className={`paysheet__method-opt ${method === 'bank' ? 'paysheet__method-opt--on' : ''}`}
        onClick={() => !disabled && onChange('bank')}
      >
        🏦 Bank
      </button>
    </div>
  );
}

function PaymentForm({ planLabel, ctaLabel, method, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');

  // Card networks don't verify the cardholder name, but ACH (bank) REQUIRES an
  // account-holder name, and it's expected on any checkout. We collect it here
  // and hand it to Stripe as billing_details.name — so the PaymentElement is
  // told name:'never' (below) to avoid a duplicate field.
  const nameLabel = method === 'bank' ? 'Account holder name' : 'Name on card';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!name.trim()) { setError(`Please enter the ${nameLabel.toLowerCase()}.`); return; }
    setSubmitting(true);
    setError('');
    // redirect:'if_required' keeps us on-page for card payments. (Only certain
    // bank-redirect methods would navigate away; cards never do.)
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { payment_method_data: { billing_details: { name: name.trim() } } },
      redirect: 'if_required',
    });
    if (result.error) {
      setError(result.error.message || (method === 'bank'
        ? 'Bank payment could not be set up. Check your details and try again.'
        : 'Payment could not be completed. Check your card and try again.'));
      setSubmitting(false);
      return;
    }
    onSuccess?.(result);
  };

  return (
    <form onSubmit={handleSubmit} className="paysheet__form">
      <label className="paysheet__field">
        <span className="paysheet__label">{nameLabel}</span>
        <input
          className="paysheet__input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={method === 'bank' ? 'Full name on the account' : 'Full name as shown on the card'}
          autoComplete="name"
          disabled={submitting}
        />
      </label>
      <PaymentElement options={{ layout: 'tabs', fields: { billingDetails: { name: 'never' } } }} />
      {method === 'bank' && (
        <p className="paysheet__method-note">
          Bank payments (ACH) take about 4 business days to process. Stripe verifies your
          account — instantly with your bank login, or with two small test deposits.
        </p>
      )}
      {error && <p className="paysheet__error" role="alert">{error}</p>}
      <button type="submit" className="paysheet__pay" disabled={!stripe || submitting}>
        {submitting ? 'Processing…' : (ctaLabel || `Start ${planLabel}`)}
      </button>
      <button type="button" className="paysheet__cancel" onClick={onCancel} disabled={submitting}>
        Cancel
      </button>
      <p className="paysheet__secure">
        <span aria-hidden>🔒</span> Secured by Stripe. Your {method === 'bank' ? 'bank' : 'card'} details never touch our servers.
      </p>
    </form>
  );
}

// userId/email props: the signup wizard passes the just-created account's
// identity explicitly — at that point nothing is in localStorage yet (the
// session only persists when the wizard finishes). All other callers omit
// them and get the stored-identity behavior unchanged.
// onSuccess(result, { subscriptionId, intentType }) — the 2nd arg lets the
// wizard verify activation server-side; existing callers ignore it.
export default function PaymentSheet({ plan = 'subscription', planLabel = 'membership', priceLabel = '', ctaLabel = '', userId: userIdProp = '', email: emailProp = '', onSuccess, onClose }) {
  const [publishableKey, setPublishableKey] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [subMeta, setSubMeta] = useState(null); // { subscriptionId, intentType }
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState('card'); // 'card' | 'bank' — the slider

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const base = (getApiBase() || '').replace(/\/$/, '');
        const userId = userIdProp || getAriaUserIdForApi() || '';
        const email = emailProp || (typeof localStorage !== 'undefined'
          && (localStorage.getItem('aria_user_email') || localStorage.getItem('aria_session_email'))) || '';
        if (!userId || !email) throw new Error('Please sign in first so we can attach your plan to your account.');

        // 1. Publishable key (public — safe in the browser).
        const sres = await fetch(`${base}/api/stripe/status`);
        const sdata = await sres.json().catch(() => ({}));
        const pk = sdata?.publishableKey || '';
        if (!sdata?.configured) throw new Error('Payments aren\'t turned on yet. Add your Stripe keys to the backend .env.');
        if (!pk) throw new Error('Missing Stripe publishable key (STRIPE_PUBLISHABLE_KEY in the backend .env).');

        // 2. Client secret for the embedded form (server creates the
        //    subscription). `method` picks the single rail the form shows —
        //    flipping the slider re-runs this effect and mints a new intent.
        const ires = await fetch(`${base}/api/stripe/subscribe-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, email, plan, method }),
        });
        const idata = await ires.json().catch(() => ({}));
        if (!idata?.ok || !idata.clientSecret) throw new Error(idata?.error || 'Could not start checkout. Please try again.');

        if (cancelled) return;
        setPublishableKey(pk);
        setClientSecret(idata.clientSecret);
        setSubMeta({ subscriptionId: idata.subscriptionId || '', intentType: idata.intentType || '' });
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Could not start checkout.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [plan, method]);

  const stripePromise = publishableKey ? stripePromiseFor(publishableKey) : null;

  const sheet = (
    <div className="paysheet__backdrop" onClick={onClose} role="presentation">
      <div className="paysheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Payment">
        <button type="button" className="paysheet__close" onClick={onClose} aria-label="Close">×</button>
        <h2 className="paysheet__title">{planLabel}</h2>
        {priceLabel && <p className="paysheet__price">{priceLabel}</p>}

        <MethodSwitch method={method} onChange={setMethod} disabled={loading} />

        {loading && <p className="paysheet__loading">Loading secure checkout…</p>}
        {loadError && (
          <>
            <p className="paysheet__error" role="alert">{loadError}</p>
            <button type="button" className="paysheet__cancel" onClick={onClose}>Close</button>
          </>
        )}

        {!loading && !loadError && clientSecret && stripePromise && (
          <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret, appearance: APPEARANCE }}>
            <PaymentForm planLabel={planLabel} ctaLabel={ctaLabel} method={method} onSuccess={(res) => onSuccess?.(res, subMeta)} onCancel={onClose} />
          </Elements>
        )}
      </div>
    </div>
  );

  // Portal to <body> so the sheet escapes the subscription section's stacking
  // contexts (.app-subscription-section has a transform — which even
  // re-anchors position:fixed to itself — and .app-content is z-index:1). Only
  // in the root context does the sheet's z-index 100010 beat the AuthGate's
  // 99999. Guard the target: if <body> is somehow unavailable, render inline
  // rather than throw "Target container is not a DOM element" (which would trip
  // the app-wide error boundary and take the whole UI down).
  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  return portalTarget ? createPortal(sheet, portalTarget) : sheet;
}
